import { MockDataScenario, mockCeleryData } from "../../mock-data/celery-tasks";
import { getGlobalHeadersAsObject } from "../global-headers";
import type {
	CeleryTaskPaginatedResponse,
	CeleryTaskQueryParams,
	CeleryTaskResponse,
} from "./types";
import { CelerySortByAttribute, CeleryState, getCeleryStateColors } from "./types";

export type { CeleryTaskResponse, CeleryTaskQueryParams, CeleryTaskPaginatedResponse };
export { CeleryState, CelerySortByAttribute, getCeleryStateColors };

// Chrome extension storage interface for webapp context
// (window.chrome may exist when extension is installed)
interface ChromeStorageLocal {
	get: (
		keys: string[] | string,
		callback: (result: Record<string, unknown>) => void
	) => void;
	set: (items: object, callback?: () => void) => void;
}

interface ChromeStorageApi {
	storage?: {
		local?: ChromeStorageLocal;
	};
}

function getChromeStorage(): ChromeStorageLocal | undefined {
	if (typeof window === "undefined") return undefined;
	const chromeApi = (window as Window & { chrome?: ChromeStorageApi }).chrome;
	return chromeApi?.storage?.local;
}

// Function to generate a UUID v4 (same implementation as in background.js)
function generateUUID(): string {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

// Function to get or create device ID using either Chrome storage or localStorage
export const getDeviceId = async (): Promise<string> => {
	let deviceId: string | null = null;

	// First check localStorage - this is our primary source in the webapp
	try {
		deviceId = localStorage.getItem("device_id");
	} catch (error) {
		console.error("Error accessing localStorage:", error);
	}

	// If we have the Chrome extension available, check its storage
	const chromeStorage = getChromeStorage();
	if (chromeStorage) {
		try {
			// Try to get existing device ID from Chrome storage
			const extensionDeviceId = await new Promise<string | null>((resolve) => {
				chromeStorage.get("device_id", function (result) {
					resolve((result.device_id as string) || null);
				});
			});

			if (extensionDeviceId) {
				console.log("Found device ID in Chrome extension:", extensionDeviceId);

				// If we don't have a device ID in localStorage yet, use extension's
				if (!deviceId) {
					// Use the extension's device ID
					deviceId = extensionDeviceId;

					// Update localStorage with the extension's device ID
					try {
						localStorage.setItem("device_id", deviceId);
						console.log(
							"Updated localStorage with device ID from extension:",
							deviceId
						);
					} catch (err) {
						console.error("Error storing device ID in localStorage:", err);
					}
				}
				// If we already have a device ID in localStorage AND it's different from extension's
				else if (deviceId !== extensionDeviceId) {
					// IMPORTANT: Prefer localStorage ID that was likely created first
					console.log(
						"Keeping existing localStorage device ID instead of extension ID"
					);

					// Update extension with our localStorage device ID
					try {
						chromeStorage.set({ device_id: deviceId }, function () {
							console.log(
								"Updated extension with device ID from localStorage:",
								deviceId
							);
						});
					} catch (err) {
						console.error("Error updating device ID in extension:", err);
					}
				}
			}
			// If we have a device ID from localStorage but not in the extension
			else if (deviceId) {
				// Store our localStorage device ID in the extension
				try {
					chromeStorage.set({ device_id: deviceId }, function () {
						console.log(
							"Updated extension with device ID from localStorage:",
							deviceId
						);
					});
				} catch (err) {
					console.error("Error storing device ID in extension:", err);
				}
			}
		} catch (error) {
			console.error("Error accessing Chrome extension storage:", error);
		}
	}

	// If no device ID found anywhere, generate a new one
	if (!deviceId) {
		deviceId = generateUUID();
		console.log("Generated new device ID:", deviceId);

		// Store in localStorage
		try {
			localStorage.setItem("device_id", deviceId);
			console.log("Stored new device ID in localStorage");
		} catch (err) {
			console.error("Error storing device ID in localStorage:", err);
		}

		// Also store in extension if available
		if (chromeStorage) {
			try {
				chromeStorage.set({ device_id: deviceId }, function () {
					console.log("Stored new device ID in extension storage");
				});
			} catch (err) {
				console.error("Error storing device ID in extension:", err);
			}
		}
	}

	return deviceId;
};

// Helper function to check if we should use mock data
const getMockDataScenario = (): MockDataScenario | null => {
	if (typeof window === "undefined") return null;
	const urlParams = new URLSearchParams(window.location.search);
	const mockData = urlParams.get("mockData") as MockDataScenario;
	return mockData && Object.keys(mockCeleryData).includes(mockData) ? mockData : null;
};

const buildQueryParams = (queryParams: CeleryTaskQueryParams): URLSearchParams => {
	const params = new URLSearchParams();
	if (queryParams.limit) {
		params.append("limit", queryParams.limit);
	}
	if (queryParams.offset) {
		params.append("offset", queryParams.offset);
	}
	if (queryParams.sort_by) {
		params.append("sort_by", queryParams.sort_by);
	}
	if (queryParams.workername) {
		params.append("workername", queryParams.workername);
	}
	if (queryParams.taskname) {
		for (let taskname of queryParams.taskname) {
			if (taskname) {
				params.append("taskname", taskname);
			}
		}
	}
	if (queryParams.state) {
		for (let state of queryParams.state) {
			if (state) {
				params.append("state", state);
			}
		}
	}
	if (queryParams.not_state) {
		for (let not_state of queryParams.not_state) {
			if (not_state) {
				params.append("not_state", not_state);
			}
		}
	}
	if (queryParams.received_start) {
		params.append("received_start", queryParams.received_start);
	}
	if (queryParams.received_end) {
		params.append("received_end", queryParams.received_end);
	}
	if (queryParams.assignment_id) {
		params.append("assignment_id", queryParams.assignment_id);
	}
	return params;
};

export const getCeleryQueueStats = async (): Promise<{
	queue_length: number;
	active_workers: number;
	reserved_tasks: number;
	active_tasks: number;
	pending_tasks: number;
	per_queue?: {
		[queue_name: string]: {
			active_tasks: number;
			reserved_tasks: number;
			workers: number;
			pending_tasks: number;
		};
	};
	broker?: {
		host: string;
		ip: string | null;
		port: number | null;
		vhost: string;
		scheme: string;
	};
}> => {
	// Check for mock data
	const mockScenario = getMockDataScenario();
	if (mockScenario) {
		console.log(`Using mock celery stats data: ${mockScenario}`);
		return Promise.resolve(mockCeleryData[mockScenario].stats);
	}

	const URL = "/api/v2/celeryqueue2/stats/";

	return fetch(URL, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then(async (res) => {
		if (!res.ok) {
			throw new Error(`HTTP ${res.status}: ${res.statusText}`);
		}
		return res.json();
	});
};

export const getCeleryQueue2 = async ({
	queryParams,
}: {
	queryParams: CeleryTaskQueryParams;
}): Promise<CeleryTaskPaginatedResponse> => {
	// Check for mock data
	const mockScenario = getMockDataScenario();
	if (mockScenario) {
		console.log(`Using mock celery queue data (Direct): ${mockScenario}`);
		return Promise.resolve({
			results: mockCeleryData[mockScenario].tasks,
			count: mockCeleryData[mockScenario].tasks.length,
		});
	}

	const URL = "/api/v2/celeryqueue2/";
	const params = buildQueryParams(queryParams);

	// Get device ID - will always return a value now
	const deviceId = await getDeviceId();
	params.append("device_id", deviceId);

	return fetch(`${URL}?${params.toString()}`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then(async (res) => {
		if (!res.ok) {
			throw new Error(`HTTP ${res.status}: ${res.statusText}`);
		}
		return res.json();
	});
};

export const revokeCeleryTask = async ({
	taskId,
}: {
	taskId: string;
}): Promise<void> => {
	const URL = "/api/v2/celery_revoke_task";

	// Get device ID - will always return a value now
	const deviceId = await getDeviceId();
	const deviceParam = `&device_id=${deviceId}`;

	return fetch(`${URL}/${taskId}/?terminate=True${deviceParam}`, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (res.ok) {
			return;
		}
		throw Error("Something went wrong revoking task");
	});
};

export const deleteTaskResult = async ({
	taskId,
}: {
	taskId: string;
}): Promise<void> => {
	const URL = `/api/v2/taskresult/${taskId}/`;

	return fetch(URL, {
		method: "DELETE",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => {
		if (res.ok) {
			return;
		}
		throw Error("Failed to delete task result");
	});
};

export const purgeCeleryQueue = async ({
	queue,
}: {
	queue: string;
}): Promise<{ queue: string; purged: number }> => {
	const URL = "/api/v2/celeryqueue2/purge/";

	// Get device ID - keep consistent logging/pings
	const deviceId = await getDeviceId();
	const params = new URLSearchParams({ queue, device_id: deviceId });

	return fetch(`${URL}?${params.toString()}`, {
		method: "POST",
		headers: getGlobalHeadersAsObject(),
	}).then(async (res) => {
		if (!res.ok) {
			const text = await res.text();
			throw Error(text || "Failed to purge queue");
		}
		return res.json();
	});
};

// Task triggering types and functions

export interface TaskParam {
	name: string;
	type: string; // 'int', 'bool', 'str', 'list', 'dict', 'any', etc.
	required: boolean;
	default: unknown | null;
}

export interface TaskDefinition {
	name: string;
	params: TaskParam[];
	doc: string;
}

export interface TriggerTaskResponse {
	task_id: string;
	task_name: string;
	status: string;
}

/**
 * Get all available Celery tasks with their parameter schemas.
 */
export const getAvailableTasks = async (): Promise<TaskDefinition[]> => {
	const URL = "/api/v2/celery_trigger/available/";

	return fetch(URL, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then(async (res) => {
		if (!res.ok) {
			const text = await res.text();
			throw Error(text || "Failed to fetch available tasks");
		}
		return res.json();
	});
};

/**
 * Trigger a Celery task with the given parameters.
 */
export const triggerCeleryTask = async ({
	taskName,
	kwargs,
}: {
	taskName: string;
	kwargs: Record<string, unknown>;
}): Promise<TriggerTaskResponse> => {
	const URL = "/api/v2/celery_trigger/trigger/";

	return fetch(URL, {
		method: "POST",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			task_name: taskName,
			kwargs: kwargs,
		}),
	}).then(async (res) => {
		if (!res.ok) {
			const data = await res.json().catch(() => ({}));
			throw Error(data.error || "Failed to trigger task");
		}
		return res.json();
	});
};
